import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { ModelCodeService } from 'src/app/services/model-code/model-code.service';
import { ModelCodeModalService } from 'src/app/services/model-code-modal/model-code-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-model-codes',
  templateUrl: './model-codes.component.html',
  styleUrls: ['./model-codes.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ModelCodesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  modelCodes: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedModelCodes: string[] = [];

  constructor(
    private _modelCodeService: ModelCodeService,
    private _modelCodeModalService: ModelCodeModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadModelCodes();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadModelCodes(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedModelCodes = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._modelCodeService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.modelCodes = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.modelCodes = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading model codes:', error);
          this.modelCodes = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadModelCodes(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadModelCodes(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadModelCodes();
  }

  openAddModelCodeModal(): void {
    this._modelCodeModalService.clearSelectedModelCode();
    const modalElement = document.getElementById('kt_modal_model_code_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(modelCode: any): void {
    this._modelCodeModalService.setSelectedModelCode(modelCode);
    const modalElement = document.getElementById('kt_modal_model_code_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteModelCode(modelCode: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${modelCode.code}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(modelCode);
      }
    });
  }

  private performDelete(modelCode: any): void {
    const modelCodeId = modelCode._id || modelCode.id;
    this.subscriptions.push(
      this._modelCodeService.DeleteDataById(modelCodeId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Model code has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadModelCodes(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete model code.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting model code.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onModelCodeAdded(): void {
    console.log('Model code added, refreshing list...');
    this.loadModelCodes(this.currentPage, this.searchTerm, this.filters);
  }

  onModelCodeUpdated(): void {
    console.log('Model code updated, refreshing list...');
    this.loadModelCodes(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(modelCodeId: string): boolean {
    return this.selectedModelCodes.includes(modelCodeId);
  }

  isAllSelected(): boolean {
    return this.modelCodes.length > 0 && this.selectedModelCodes.length === this.modelCodes.length;
  }

  isIndeterminate(): boolean {
    return this.selectedModelCodes.length > 0 && this.selectedModelCodes.length < this.modelCodes.length;
  }

  toggleSelection(modelCodeId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedModelCodes.includes(modelCodeId)) {
        this.selectedModelCodes.push(modelCodeId);
      }
    } else {
      this.selectedModelCodes = this.selectedModelCodes.filter(id => id !== modelCodeId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedModelCodes = this.modelCodes.map(modelCode => modelCode._id);
    } else {
      this.selectedModelCodes = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-model-code-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-model-code-table-toolbar="selected"]');

    if (this.selectedModelCodes.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedModelCodes(): void {
    if (this.selectedModelCodes.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedModelCodes.length} selected model code(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedModelCodes.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._modelCodeService.BulkDelete(this.selectedModelCodes).subscribe({
        next: () => {
          this.loadModelCodes();
          this.selectedModelCodes = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected model codes have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting model codes:', error);
          Swal.fire('Error!', 'Failed to delete selected model codes.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
